import React from "react";
import { ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";

const SERVICES = [
  {
    title: "Liberación de amarres",
    body: "Gestión de casos de embarcaciones que ocupan amarres por impago, abandono o falta de resolución administrativa o judicial.",
  },
  {
    title: "Procedimiento judicial y venta",
    body: "Tramitación orientada a obtener autorización judicial para la venta de la embarcación mediante entidad especializada, cuando proceda.",
  },
  {
    title: "Reclamación de deudas",
    body: "Apoyo en la reclamación judicial de importes derivados de impagos de amarres y otros conceptos asociados.",
  },
];

const SERVICES_IMG =
  "https://images.pexels.com/photos/33036859/pexels-photo-33036859.jpeg?auto=compress&cs=tinysrgb&w=1600";

const Services = () => {
  return (
    <section
      id="servicios"
      data-testid="section-services"
      className="py-24 sm:py-32 bg-surface-alt relative overflow-hidden"
    >
      {/* Decorative watermark */}
      <div className="absolute left-0 -top-8 font-display text-[14rem] lg:text-[18rem] leading-none text-navy/[0.025] select-none pointer-events-none -ml-6">
        02
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-sky" />
                <div className="text-xs uppercase tracking-[0.28em] text-sky font-semibold">
                  02 — Servicios
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy leading-[1.1] tracking-tight">
                Una solución <span className="italic text-navy/70">integral</span> para puertos,
                marinas y clubes náuticos.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-ink-muted text-base lg:text-lg leading-relaxed">
                Ofrecemos una solución integral para puertos, marinas y clubes náuticos que
                necesitan resolver situaciones de ocupación de amarres, impago o abandono de
                embarcaciones.
              </p>
            </Reveal>
          </div>
          <Reveal delay={200} className="lg:col-span-5 hidden lg:block">
            <div className="relative aspect-[5/4] rounded-sm overflow-hidden shadow-2xl group">
              <img
                src={SERVICES_IMG}
                alt="Marina con embarcaciones amarradas"
                className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105"
                data-testid="services-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-navy/10" />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy-900/50 to-transparent" />
            </div>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.title} delay={idx * 120}>
              <article
                data-testid={`service-card-${idx}`}
                className="group relative h-full bg-white p-8 lg:p-10 pt-12 lg:pt-14 rounded-sm border border-slate-200 hover:border-navy/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                {/* Top accent line — fills on hover */}
                <div className="absolute top-0 left-0 h-[2px] w-0 bg-gradient-to-r from-sky to-navy group-hover:w-full transition-all duration-700" />

                {/* Hover arrow indicator */}
                <div className="absolute top-8 right-8 text-navy/0 group-hover:text-sky transition-all duration-500 -translate-x-2 group-hover:translate-x-0">
                  <ArrowUpRight size={22} strokeWidth={1.5} />
                </div>

                {/* Editorial numeral — replaces the icon */}
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-6xl lg:text-7xl text-navy leading-none tabular-nums tracking-tighter group-hover:text-sky transition-colors duration-500">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-2xl text-sky/40 leading-none">/</span>
                  <span className="text-[11px] uppercase tracking-[0.28em] text-ink-soft font-semibold pb-1.5">
                    Servicio
                  </span>
                </div>

                {/* Refined divider */}
                <div className="mt-8 h-px w-12 bg-slate-300 group-hover:w-24 group-hover:bg-sky transition-all duration-500" />

                <h3 className="mt-6 font-display text-2xl text-navy leading-snug">
                  {s.title}
                </h3>
                <p className="mt-4 text-ink-muted text-[15px] leading-relaxed">
                  {s.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
